import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Stack,
} from '@mui/material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import useSidebar from '../hooks/useSidebar';
import api from '../services/api';
import { useBranding } from '../context/BrandingContext';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Comentários em português; código em inglês
// Página administrativa para gerenciar o branding (logos e textos)

const UploadField = ({ label, slot, variant, onUploaded, onRemoved, previewSrc, showLabel = false }) => {
	const [loading, setLoading] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [error, setError] = useState('');
	const inputRef = React.useRef(null);

	const handleUpload = async (file) => {
		if (!file) return;
		setLoading(true);
		setError('');
		try {
			const form = new FormData();
			// Enviar campos primeiro para garantir que o backend conheça slot/variant antes do arquivo
			form.append('slot', slot);
			if (variant) form.append('variant', variant);
			form.append('file', file);
			// Não definir manualmente o Content-Type; deixar o axios adicionar o boundary
			const res = await api.post('/branding/assets', form);
			onUploaded && onUploaded(res.data?.data);
		} catch (e) {
			setError(e.response?.data?.message || 'Erro ao enviar arquivo');
		} finally {
			setLoading(false);
			if (inputRef.current) inputRef.current.value = '';
		}
	};

	const handleFileChange = (e) => {
		const f = e.target.files?.[0];
		if (f) handleUpload(f);
	};

	const handleRemove = async () => {
		if (!previewSrc) return;
		try {
			if (!window.confirm('Remover esta imagem?')) return;
			setRemoving(true);
			await api.delete('/branding/assets', { params: { slot, variant } });
			onRemoved && onRemoved();
		} catch (e) {
			setError(e.response?.data?.message || 'Erro ao remover imagem');
		} finally {
			setRemoving(false);
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			{showLabel && (
				<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>
			)}
			<input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => inputRef.current && inputRef.current.click()} disabled={loading || removing}>
					{loading ? 'Enviando...' : 'Editar'}
				</Button>
				<Button variant="text" color="error" size="small" startIcon={<DeleteIcon />} onClick={handleRemove} disabled={!previewSrc || loading || removing}>
					{removing ? 'Removendo...' : 'Remover'}
				</Button>
			</Box>
			{error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
		</Box>
	);
};

const UploadCard = ({
	label,
	slot,
	variant,
	onUploaded,
	previewSrc,
	recommended,
	widthValue,
	heightValue,
	onWidthChange,
	onHeightChange
}) => {
	return (
		<Card variant="outlined" sx={{ height: '100%' }}>
			<CardHeader title={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}</Typography>} />
			<CardContent>
				<Stack spacing={1.5}>
					<Box sx={{
						border: '1px dashed',
						borderColor: 'divider',
						borderRadius: 1,
						p: 1,
						height: 120,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'background.default'
					}}>
						{previewSrc ? (
							<Box component="img" src={previewSrc} alt={label} sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
						) : (
							<Typography variant="caption" color="text.secondary">Sem imagem</Typography>
						)}
					</Box>
					{(onWidthChange || onHeightChange) && (
						<Grid container spacing={1}>
							<Grid item xs={6}>
								<TextField type="number" fullWidth label="Largura (px)" value={widthValue ?? ''} onChange={e => onWidthChange && onWidthChange(e.target.value)} />
							</Grid>
							<Grid item xs={6}>
								<TextField type="number" fullWidth label="Altura (px)" value={heightValue ?? ''} onChange={e => onHeightChange && onHeightChange(e.target.value)} />
							</Grid>
						</Grid>
					)}
					{recommended && (
						<Typography variant="caption" color="text.secondary">{recommended}</Typography>
					)}
				</Stack>
			</CardContent>
			<CardActions sx={{ justifyContent: 'space-between' }}>
				<UploadField label={label} slot={slot} variant={variant} onUploaded={onUploaded} onRemoved={onUploaded} previewSrc={previewSrc} />
			</CardActions>
		</Card>
	);
};

const Branding = () => {
	const { sidebarOpen, toggleSidebar } = useSidebar();
	const { branding } = useBranding();
	const [form, setForm] = useState({
		displayName: '',
		displaySubtitle: '',
	});
	const [sizes, setSizes] = useState({
		sidebar: { light: { width: 60, height: 60 }, dark: { width: 60, height: 60 } },
		sidebarOpen: { light: { width: 60, height: 60 }, dark: { width: 60, height: 60 } },
		sidebarClosed: { light: { width: 45, height: 45 }, dark: { width: 45, height: 45 } },
		login: { main: { width: 200, height: 80 } },
		panel: { main: { width: 200, height: 80 } },
		reports: { header: { width: 300, height: 80 }, footer: { width: 300, height: 80 } },
	});
	const [textSizes, setTextSizes] = useState({
		sidebar: { titlePx: 18, subtitlePx: 10 },
		login: { titlePx: 22, subtitlePx: 14 },
		panel: { titlePx: 22, subtitlePx: 13 },
	});
	const [textVisibility, setTextVisibility] = useState({
		showTitle: true,
		showSubtitle: true,
	});
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		if (branding) {
			setForm({
				displayName: branding.displayName || '',
				displaySubtitle: branding.displaySubtitle || '',
			});
			if (branding.sizing) {
				setSizes(prev => ({ ...prev, ...branding.sizing }));
			}
			if (branding.textSizing) {
				setTextSizes(prev => ({ ...prev, ...branding.textSizing }));
			}
			if (branding.textVisibility) {
				setTextVisibility(prev => ({ ...prev, ...branding.textVisibility }));
			}
		}
	}, [branding]);

	const handleSave = async () => {
		setMessage('');
		setError('');
		try {
			const res = await api.patch('/branding', {
				displayName: form.displayName,
				displaySubtitle: form.displaySubtitle,
				data: { sizing: sizes, textSizing: textSizes, textVisibility },
			});
			setMessage('Configuração salva com sucesso');
		} catch (e) {
			setError(e.response?.data?.message || 'Erro ao salvar configuração');
		}
	};

	return (
		<Box sx={{ display: 'flex', minHeight: '100vh' }}>
			<Sidebar sidebarOpen={sidebarOpen} />
			<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
				<Header sidebarOpen={sidebarOpen} handleSidebarToggle={toggleSidebar} title="Branding" />
				<Container sx={{ mt: 10, mb: 4 }}>
					<Paper sx={{ p: 3 }}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Textos</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField fullWidth label="Nome exibido" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField fullWidth label="Subtítulo" value={form.displaySubtitle} onChange={e => setForm(f => ({ ...f, displaySubtitle: e.target.value }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Sidebar: Título (px)" value={textSizes.sidebar?.titlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, sidebar: { ...s.sidebar, titlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Sidebar: Subtítulo (px)" value={textSizes.sidebar?.subtitlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, sidebar: { ...s.sidebar, subtitlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Login: Título (px)" value={textSizes.login?.titlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, login: { ...s.login, titlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Login: Subtítulo (px)" value={textSizes.login?.subtitlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, login: { ...s.login, subtitlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Painel: Título (px)" value={textSizes.panel?.titlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, panel: { ...s.panel, titlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField type="number" fullWidth label="Painel: Subtítulo (px)" value={textSizes.panel?.subtitlePx ?? ''} onChange={e => setTextSizes(s => ({ ...s, panel: { ...s.panel, subtitlePx: Number(e.target.value) || undefined } }))} />
							</Grid>
							<Grid item xs={12} md={12}>
								<Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Visibilidade por contexto</Typography>
							</Grid>
							<Grid item xs={12} md={4}>
								<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Sidebar</Typography>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<label><input type="checkbox" checked={textVisibility.sidebar?.showTitle ?? textVisibility.showTitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, sidebar: { ...(v.sidebar||{}), showTitle: e.target.checked } }))} /> Título</label>
									<label><input type="checkbox" checked={textVisibility.sidebar?.showSubtitle ?? textVisibility.showSubtitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, sidebar: { ...(v.sidebar||{}), showSubtitle: e.target.checked } }))} /> Subtítulo</label>
								</Box>
							</Grid>
							<Grid item xs={12} md={4}>
								<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Login</Typography>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<label><input type="checkbox" checked={textVisibility.login?.showTitle ?? textVisibility.showTitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, login: { ...(v.login||{}), showTitle: e.target.checked } }))} /> Título</label>
									<label><input type="checkbox" checked={textVisibility.login?.showSubtitle ?? textVisibility.showSubtitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, login: { ...(v.login||{}), showSubtitle: e.target.checked } }))} /> Subtítulo</label>
								</Box>
							</Grid>
							<Grid item xs={12} md={4}>
								<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Painel</Typography>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<label><input type="checkbox" checked={textVisibility.panel?.showTitle ?? textVisibility.showTitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, panel: { ...(v.panel||{}), showTitle: e.target.checked } }))} /> Título</label>
									<label><input type="checkbox" checked={textVisibility.panel?.showSubtitle ?? textVisibility.showSubtitle ?? true} onChange={e => setTextVisibility(v => ({ ...v, panel: { ...(v.panel||{}), showSubtitle: e.target.checked } }))} /> Subtítulo</label>
								</Box>
							</Grid>
						</Grid>
						<Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
							<Button variant="contained" onClick={handleSave}>Salvar</Button>
						</Box>
						{message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
						{error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

						<Divider sx={{ my: 3 }} />

						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Logos</Typography>
						<Grid container spacing={2}>

							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Sidebar Aberta (Light)" 
									slot="sidebar-open" 
									variant="light"
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.sidebarOpen?.light}
									widthValue={sizes.sidebarOpen?.light?.width}
									heightValue={sizes.sidebarOpen?.light?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, sidebarOpen: { ...s.sidebarOpen, light: { ...s.sidebarOpen?.light, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, sidebarOpen: { ...s.sidebarOpen, light: { ...s.sidebarOpen?.light, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Sidebar Aberta (Dark)" 
									slot="sidebar-open" 
									variant="dark"
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.sidebarOpen?.dark}
									widthValue={sizes.sidebarOpen?.dark?.width}
									heightValue={sizes.sidebarOpen?.dark?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, sidebarOpen: { ...s.sidebarOpen, dark: { ...s.sidebarOpen?.dark, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, sidebarOpen: { ...s.sidebarOpen, dark: { ...s.sidebarOpen?.dark, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Sidebar Fechada (Light)" 
									slot="sidebar-closed" 
									variant="light"
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.sidebarClosed?.light}
									widthValue={sizes.sidebarClosed?.light?.width}
									heightValue={sizes.sidebarClosed?.light?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, sidebarClosed: { ...s.sidebarClosed, light: { ...s.sidebarClosed?.light, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, sidebarClosed: { ...s.sidebarClosed, light: { ...s.sidebarClosed?.light, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Sidebar Fechada (Dark)" 
									slot="sidebar-closed" 
									variant="dark"
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.sidebarClosed?.dark}
									widthValue={sizes.sidebarClosed?.dark?.width}
									heightValue={sizes.sidebarClosed?.dark?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, sidebarClosed: { ...s.sidebarClosed, dark: { ...s.sidebarClosed?.dark, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, sidebarClosed: { ...s.sidebarClosed, dark: { ...s.sidebarClosed?.dark, height: Number(v) || undefined } } }))}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Login" 
									slot="login" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.login?.main}
									widthValue={sizes.login?.main?.width}
									heightValue={sizes.login?.main?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, login: { main: { ...s.login?.main, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, login: { main: { ...s.login?.main, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Painel" 
									slot="panel" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.panel?.main}
									widthValue={sizes.panel?.main?.width}
									heightValue={sizes.panel?.main?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, panel: { main: { ...s.panel?.main, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, panel: { main: { ...s.panel?.main, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Relatórios (Header)" 
									slot="reports-header" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.reports?.header}
									widthValue={sizes.reports?.header?.width}
									heightValue={sizes.reports?.header?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, reports: { ...s.reports, header: { ...s.reports?.header, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, reports: { ...s.reports, header: { ...s.reports?.header, height: Number(v) || undefined } } }))}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<UploadCard 
									label="Relatórios (Footer)" 
									slot="reports-footer" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.reports?.footer}
									widthValue={sizes.reports?.footer?.width}
									heightValue={sizes.reports?.footer?.height}
									onWidthChange={(v) => setSizes(s => ({ ...s, reports: { ...s.reports, footer: { ...s.reports?.footer, width: Number(v) || undefined } } }))}
									onHeightChange={(v) => setSizes(s => ({ ...s, reports: { ...s.reports, footer: { ...s.reports?.footer, height: Number(v) || undefined } } }))}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ my: 3 }} />

						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Ícones</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={4}>
								<UploadCard 
									label="Favicon 16x16" 
									slot="icon-favicon16" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.icons?.favicon16}
									recommended="PNG 16x16"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UploadCard 
									label="Favicon 32x32" 
									slot="icon-favicon32" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.icons?.favicon32}
									recommended="PNG 32x32"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UploadCard 
									label="Apple Touch 180x180" 
									slot="icon-apple180" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.icons?.appleTouchIcon180}
									recommended="PNG 180x180"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UploadCard 
									label="PWA 192x192" 
									slot="icon-pwa192" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.icons?.pwaIcon192}
									recommended="PNG 192x192 (maskable recomendado)"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<UploadCard 
									label="PWA 512x512" 
									slot="icon-pwa512" 
									onUploaded={() => window.location.reload()} 
									previewSrc={branding?.assets?.icons?.pwaIcon512}
									recommended="PNG 512x512 (maskable recomendado)"
								/>
							</Grid>
						</Grid>
					</Paper>
				</Container>
			</Box>
		</Box>
	);
};

export default Branding;
